import { useState, useEffect } from 'react'
import { FiDownload, FiTrash2, FiEdit2, FiLoader, FiStar, FiX } from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'

const RESUME_STATUSES = ['submitted', 'reviewed', 'shortlisted', 'rejected']

export default function ResumesManagement({ website }) {
  const [resumes, setResumes] = useState([])
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState(null)
  const [filterStatus, setFilterStatus] = useState('all')
  const [selectedResume, setSelectedResume] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [editData, setEditData] = useState({})

  useEffect(() => {
    fetchResumes()
    fetchStats()
  }, [website])

  const fetchResumes = async () => {
    try {
      setLoading(true)
      const response = await axios.get(`/resumes/website/${website._id}`)
      setResumes(response.data.resumes || [])
      console.log('[ResumesManagement] Loaded:', response.data.resumes?.length || 0)
    } catch (error) {
      console.error('[ResumesManagement] Error loading:', error)
      toast.error('Failed to load resumes')
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await axios.get(`/resumes/stats/${website._id}`)
      setStats(response.data.stats)
    } catch (error) {
      console.error('[ResumesManagement] Error loading stats:', error)
    }
  }

  const handleDelete = async (resumeId) => {
    if (!window.confirm('Are you sure you want to delete this resume?')) return

    try {
      setLoading(true)
      await axios.delete(`/resumes/${resumeId}`)
      setResumes(resumes.filter(r => r._id !== resumeId))
      toast.success('Resume deleted successfully')
      fetchStats()
    } catch (error) {
      console.error('[ResumesManagement] Error deleting:', error)
      toast.error(error.response?.data?.message || 'Failed to delete resume')
    } finally {
      setLoading(false)
    }
  }

  const handleEditStart = (resume) => {
    setEditingId(resume._id)
    setEditData({
      status: resume.status,
      rating: resume.rating || '',
      adminNotes: resume.adminNotes || ''
    })
  }

  const handleQuickAction = async (resumeId, newStatus) => {
    try {
      setLoading(true)
      const response = await axios.put(`/resumes/${resumeId}`, { status: newStatus })
      setResumes(resumes.map(r => r._id === resumeId ? response.data.resume : r))
      toast.success(`Resume ${newStatus} successfully`)
      fetchStats()
    } catch (error) {
      console.error('[ResumesManagement] Error updating status:', error)
      toast.error(error.response?.data?.message || 'Failed to update resume status')
    } finally {
      setLoading(false)
    }
  }

  const handleEditSave = async () => {
    try {
      setLoading(true)
      const response = await axios.put(`/resumes/${editingId}`, editData)
      setResumes(resumes.map(r => r._id === editingId ? response.data.resume : r))
      setEditingId(null)
      toast.success('Resume updated successfully')
      fetchStats()
    } catch (error) {
      console.error('[ResumesManagement] Error updating:', error)
      toast.error(error.response?.data?.message || 'Failed to update resume')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async (resume) => {
    try {
      // Use backend endpoint for proper file download
      const response = await axios.get(`/resumes/download/${resume._id}`, {
        responseType: 'blob'
      })
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = resume.resume?.fileName || `resume-${resume.visitorName}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success('Resume downloaded successfully')
    } catch (error) {
      console.error('[ResumesManagement] Download error:', error)
      toast.error('Failed to download resume')
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'shortlisted':
        return 'bg-green-100 text-green-800'
      case 'reviewed':
        return 'bg-blue-100 text-blue-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-yellow-100 text-yellow-800'
    }
  }

  const getRatingStars = (rating) => {
    return '⭐'.repeat(rating || 0)
  }

  const filteredResumes = filterStatus === 'all' 
    ? resumes 
    : resumes.filter(r => r.status === filterStatus)

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-600 font-medium">Total Resumes</p>
            <p className="text-3xl font-bold text-blue-900">{stats.total}</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-600 font-medium">Submitted</p>
            <p className="text-3xl font-bold text-yellow-900">{stats.submitted}</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-sm text-purple-600 font-medium">Reviewed</p>
            <p className="text-3xl font-bold text-purple-900">{stats.reviewed}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-600 font-medium">Shortlisted</p>
            <p className="text-3xl font-bold text-green-900">{stats.shortlisted}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-600 font-medium">Rejected</p>
            <p className="text-3xl font-bold text-red-900">{stats.rejected}</p>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap border-b border-gray-200 pb-4">
        {['all', 'submitted', 'reviewed', 'shortlisted', 'rejected'].map(status => (
          <button
            key={status}
            type="button"
            onClick={() => setFilterStatus(status)}
            className={`px-3 py-2 text-sm font-medium transition-colors ${
              filterStatus === status
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
            {status !== 'all' && ` (${resumes.filter(r => r.status === status).length})`}
          </button>
        ))}
      </div>

      {/* Resumes List */}
      {loading && !resumes.length ? (
        <div className="text-center py-12">
          <FiLoader className="w-8 h-8 animate-spin mx-auto text-gray-400" />
        </div>
      ) : filteredResumes.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No {filterStatus !== 'all' ? filterStatus : ''} resumes</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredResumes.map((resume) => (
            <div
              key={resume._id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{resume.visitorName}</h3>
                  <p className="text-sm text-gray-600">{resume.visitorEmail}</p>
                </div>
                <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(resume.status)}`}>
                  {resume.status.charAt(0).toUpperCase() + resume.status.slice(1)}
                </span>
              </div>

              {/* Content */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 pb-4 border-b border-gray-200">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Position</p>
                  <p className="font-medium text-gray-900">{resume.position}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Phone</p>
                  <p className="font-medium text-gray-900">{resume.visitorPhone || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Submitted</p>
                  <p className="font-medium text-gray-900">
                    {new Date(resume.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">File</p>
                  <p className="font-medium text-gray-900 text-sm truncate">
                    {resume.resume?.fileName}
                  </p>
                </div>
              </div>

              {/* Cover Letter */}
              {resume.coverLetter && (
                <div className="mb-4 p-3 bg-gray-50 rounded">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Cover Letter</p>
                  <p className="text-sm text-gray-700 line-clamp-2">{resume.coverLetter}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleDownload(resume)}
                  className="inline-flex items-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded transition-colors text-sm font-medium disabled:opacity-50"
                  title="Download resume"
                  disabled={loading}
                >
                  <FiDownload className="w-4 h-4" />
                  Download
                </button>

                {resume.status !== 'shortlisted' && (
                  <button
                    type="button"
                    onClick={() => handleQuickAction(resume._id, 'shortlisted')}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-green-100 text-green-700 hover:bg-green-200 rounded transition-colors text-sm font-medium disabled:opacity-50"
                    title="Accept this resume"
                    disabled={loading}
                  >
                    ✓ Accept
                  </button>
                )}

                {resume.status !== 'rejected' && (
                  <button
                    type="button"
                    onClick={() => handleQuickAction(resume._id, 'rejected')}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded transition-colors text-sm font-medium disabled:opacity-50"
                    title="Reject this resume"
                    disabled={loading}
                  >
                    ✗ Reject
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => handleEditStart(resume)}
                  className="inline-flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded transition-colors text-sm font-medium"
                  title="Edit resume details"
                >
                  <FiEdit2 className="w-4 h-4" />
                  Details
                </button>

                <button
                  type="button"
                  onClick={() => handleDelete(resume._id)}
                  className="inline-flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded transition-colors text-sm font-medium disabled:opacity-50"
                  title="Delete resume"
                  disabled={loading}
                >
                  <FiTrash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>

              {/* Edit Panel */}
              {editingId === resume._id && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                  <h4 className="font-semibold text-gray-900">Edit Resume</h4>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={editData.status}
                        onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                        className="input-field w-full text-sm"
                      >
                        {RESUME_STATUSES.map(s => (
                          <option key={s} value={s}>
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rating (1-5 stars) {editData.rating && ` - ${getRatingStars(parseInt(editData.rating))}`}
                      </label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map(star => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setEditData({ ...editData, rating: star })}
                            className={`text-2xl transition-transform hover:scale-110 ${
                              editData.rating >= star ? 'text-yellow-400' : 'text-gray-300'
                            }`}
                          >
                            ⭐
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes</label>
                      <textarea
                        value={editData.adminNotes}
                        onChange={(e) => setEditData({ ...editData, adminNotes: e.target.value })}
                        placeholder="Add notes about this candidate..."
                        rows="3"
                        className="input-field w-full text-sm resize-none"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleEditSave}
                      disabled={loading}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded font-medium text-sm transition-colors"
                    >
                      {loading ? <FiLoader className="w-4 h-4 animate-spin inline mr-2" /> : null}
                      Save Changes
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300 rounded font-medium text-sm transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
